import { Send } from '@carbon/icons-react';
import { Button, Checkbox, InlineNotification, MenuButton, MenuItem, Tile } from '@carbon/react';
import {
  asProblem,
  hasPermission,
  ListTable,
  PageHeader,
  problemMessage,
  RowAction,
  StatusTag,
  useAdmin,
  useFormat,
} from '@pos/admin-ui';
import type { HoComponents } from '@pos/api-client';
import { ConfirmDialog } from '@pos/design-system';
import { useTranslation, type I18nKey } from '@pos/i18n';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { hoApi } from '../api';
import { TransientNotice } from '../common/Notices';
import { loadStoreGroups, loadStoreGroupStoreIds } from '../common/storeGroups';
import './sync.scss';

const statusKey = ['sync', 'master-data'] as const;

type MasterDataStoreDto = HoComponents['schemas']['MasterDataStoreDto'];
type PublishMasterDataRequest = HoComponents['schemas']['PublishMasterDataRequest'];

type Row = MasterDataStoreDto & { id: string };

/** What to publish to, confirmed before it goes: a store group is its stores at the moment it is chosen (ADR 0115). */
type Target =
  | { kind: 'store'; row: Row }
  | { kind: 'group'; name: string; storeIds: string[] }
  | { kind: 'all'; website: boolean }
  | { kind: 'website' };


/**
 * Master data on demand (ADR 0078): head office changes reach stores and the website only when published here. Each store shows
 * whether head office has changes it hasn't taken, and whether it has taken the last publish; a store that is offline takes it
 * when it is back.
 */
export function MasterDataPublishPage() {
  const { t } = useTranslation();
  const format = useFormat();
  const { session } = useAdmin();
  const canPublish = hasPermission(session ?? undefined, 'ho.sync.manage');
  const canPickGroup = canPublish && hasPermission(session ?? undefined, 'ho.org.store_groups.read');
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [paging, setPaging] = useState({ page: 1, pageSize: 25 });
  const [target, setTarget] = useState<Target>();
  const [done, setDone] = useState<string>();
  const [emptyGroup, setEmptyGroup] = useState<string>();
  const groups = useQuery({ queryKey: ['ho', 'store-groups', null], queryFn: () => loadStoreGroups(), enabled: canPickGroup });
  const pickGroup = useMutation({
    mutationFn: async (group: { id: string; name: string }) => ({ name: group.name, storeIds: await loadStoreGroupStoreIds(group.id) }),
    onSuccess: ({ name, storeIds }) => {
      if (storeIds.length === 0) setEmptyGroup(name);
      else open({ kind: 'group', name, storeIds });
    },
  });

  const status = useQuery({
    queryKey: [...statusKey, search, paging.page, paging.pageSize],
    queryFn: async () => {
      const { data, error } = await hoApi.GET('/api/v1/sync/master-data', {
        params: { query: { search: search || undefined, page: paging.page, pageSize: paging.pageSize } },
      });
      if (!data) throw error;
      return data;
    },
    placeholderData: keepPreviousData,
    refetchInterval: 15_000,
  });

  const publish = useMutation({
    mutationFn: async (body: PublishMasterDataRequest) => {
      const { data, error } = await hoApi.POST('/api/v1/sync/master-data/publish', { body });
      if (!data) throw error;
      return data;
    },
    onSuccess: (result) => {
      setTarget(undefined);
      setDone(
        result.website && Number(result.storeCount) > 0
          ? t('admin.masterData.publishedBoth', { count: Number(result.storeCount) })
          : result.website
            ? t('admin.masterData.publishedWebsite')
            : t('admin.masterData.publishedStores', { count: Number(result.storeCount) }),
      );
      void queryClient.invalidateQueries({ queryKey: statusKey });
    },
  });

  const data = status.data;
  const rows: Row[] = (data?.stores ?? []).map((s) => ({ ...s, id: s.storeId }));
  const open = (next: Target) => {
    publish.reset();
    setTarget(next);
  };
  const confirm = () => {
    if (!target) return;
    if (target.kind === 'store') publish.mutate({ storeIds: [target.row.storeId], allStores: false, website: false });
    else if (target.kind === 'group') publish.mutate({ storeIds: target.storeIds, allStores: false, website: false });
    else if (target.kind === 'all') publish.mutate({ storeIds: null, allStores: true, website: target.website });
    else publish.mutate({ storeIds: null, allStores: false, website: true });
  };

  return (
    <section className="pos-admin-page">
      <PageHeader title={t('admin.masterData.title')} description={t('admin.masterData.hint')} />
      {done && <TransientNotice kind="success" title={done} onDone={() => setDone(undefined)} />}
      {emptyGroup && (
        <TransientNotice kind="warning" title={t('admin.masterData.groupEmpty', { group: emptyGroup })} onDone={() => setEmptyGroup(undefined)} />
      )}
      {pickGroup.error && (
        <InlineNotification kind="error" role="alert" lowContrast title={problemMessage(t, asProblem(pickGroup.error))} onClose={() => pickGroup.reset()} />
      )}

      {data?.websiteAvailable && (
        <Tile className="pos-master-data__website">
          <div>
            <p className="pos-master-data__website-title">{t('admin.masterData.website')}</p>
            <p>
              {data.websitePublishedAt
                ? t('admin.masterData.websitePublishedAt', { time: format.dateTime(data.websitePublishedAt) })
                : t('admin.masterData.websiteNever')}
            </p>
          </div>
          {canPublish && (
            <Button kind="secondary" renderIcon={Send} onClick={() => open({ kind: 'website' })}>
              {t('admin.masterData.publishWebsite')}
            </Button>
          )}
        </Tile>
      )}

      <ListTable<Row>
        ariaLabel={t('admin.masterData.title')}
        loading={status.isLoading}
        error={status.error ? problemMessage(t, asProblem(status.error)) : undefined}
        onRetry={() => void status.refetch()}
        rows={rows}
        search={{
          label: t('admin.search'),
          onChange: (value) => {
            setSearch(value);
            setPaging((p) => ({ ...p, page: 1 }));
          },
        }}
        toolbarActions={
          canPublish && Number(data?.total ?? 0) > 0 ? (
            <>
              {canPickGroup && (groups.data ?? []).length > 0 && (
                <MenuButton label={t('admin.masterData.publishGroup')} kind="ghost" size="lg" menuAlignment="bottom-end" disabled={pickGroup.isPending}>
                  {(groups.data ?? []).map((g) => (
                    <MenuItem key={g.id} label={`${g.code} · ${g.name}`} onClick={() => pickGroup.mutate({ id: g.id, name: g.name })} />
                  ))}
                </MenuButton>
              )}
              <Button kind="primary" renderIcon={Send} onClick={() => open({ kind: 'all', website: Boolean(data?.websiteAvailable) })}>
                {t('admin.masterData.publishAll')}
              </Button>
            </>
          ) : undefined
        }
        columns={[
          { key: 'store', header: t('admin.masterData.columns.store'), render: (s) => `${s.code} · ${s.name}` },
          {
            key: 'changes',
            header: t('admin.masterData.columns.changes'),
            render: (s) => (
              <StatusTag status={s.changesWaiting ? 'pending' : 'active'}>
                {s.changesWaiting ? t('admin.masterData.changesWaiting') : t('admin.masterData.upToDate')}
              </StatusTag>
            ),
          },
          {
            key: 'published',
            header: t('admin.masterData.columns.published'),
            render: (s) => (s.lastPublishedAt ? format.dateTime(s.lastPublishedAt) : t('admin.masterData.never')),
          },
          {
            key: 'status',
            header: t('admin.masterData.columns.status'),
            render: (s) => t(`admin.masterData.status.${s.publishStatus}` as I18nKey),
          },
          {
            key: 'contact',
            header: t('admin.masterData.columns.contact'),
            render: (s) => (s.lastHeartbeatAt ? format.dateTime(s.lastHeartbeatAt) : t('admin.masterData.never')),
          },
        ]}
        rowActions={
          canPublish
            ? (s) => <RowAction action={t('admin.masterData.publish')} record={`${s.code} · ${s.name}`} icon={Send} onClick={() => open({ kind: 'store', row: s })} />
            : undefined
        }
        empty={{
          heading: t(search ? 'admin.noResultsTitle' : 'admin.emptyTitle'),
          description: search ? t('admin.noResultsHint') : t('admin.masterData.empty'),
        }}
        pagination={{ ...paging, total: Number(data?.total ?? 0), onChange: ({ page, pageSize }) => setPaging({ page, pageSize }) }}
      />

      {target && (
        <ConfirmDialog
          open
          danger={false}
          title={
            target.kind === 'store'
              ? t('admin.masterData.confirmStore', { store: `${target.row.code} · ${target.row.name}` })
              : target.kind === 'group'
                ? t('admin.masterData.confirmGroup', { group: target.name, count: target.storeIds.length })
                : target.kind === 'all'
                ? t('admin.masterData.confirmAll', { count: Number(data?.total ?? 0) })
                : t('admin.masterData.confirmWebsite')
          }
          confirmLabel={t('admin.masterData.publish')}
          busy={publish.isPending}
          onConfirm={confirm}
          onCancel={() => setTarget(undefined)}
        >
          <p>{t('admin.masterData.confirmBody')}</p>
          {target.kind === 'all' && data?.websiteAvailable && (
            <Checkbox
              id="master-data-website"
              labelText={t('admin.masterData.alsoWebsite')}
              checked={target.website}
              onChange={(_, { checked }) => setTarget({ kind: 'all', website: checked })}
            />
          )}
          {publish.error && (
            <InlineNotification kind="error" role="alert" lowContrast hideCloseButton title={problemMessage(t, asProblem(publish.error))} />
          )}
        </ConfirmDialog>
      )}
    </section>
  );
}
